import React, { useState, useEffect } from "react";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { track, newEventId, trackViewContent, getAdConsent } from "../lib/analytics";
import { getReferral, clearReferral } from "../lib/referral";
import { WaitlistForm } from "./Landing";
import { formatPercentOff } from "../lib/format";
import { FEE_DEFAULT, TEAM_MIN_DEFAULT, TEAM_MAX_DEFAULT } from "../lib/appDefaults";
import { Mail, Building2, User, GraduationCap, Linkedin, Phone, Gift, X, Lock } from "lucide-react";

const DISPOSABLE = ["gmail.com", "yahoo.com", "yahoo.co.in", "hotmail.com", "outlook.com", "rediffmail.com", "icloud.com", "live.com"];

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { config, user } = useAuth();
  const [mode, setMode] = useState(params.get("mode") === "login" ? "login" : "register"); // or "login"
  const [name, setName] = useState("");
  const [personalEmail, setPersonalEmail] = useState("");
  const [collegeName, setCollegeName] = useState("");
  const [collegeEmail, setCollegeEmail] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [phone, setPhone] = useState("");
  // Path A: pre-fill from the ?ref= captured in localStorage. Path B: user can type/override it.
  const [referralCode, setReferralCode] = useState(() => getReferral() || "");
  const [refPerk, setRefPerk] = useState(null);
  const [hiConsent, setHiConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  // Live discount preview (public, no-auth) — shows the checkout savings the moment a valid
  // NSA code is typed. Debounced; server recomputes the price (never trusts the client).
  useEffect(() => {
    const code = referralCode.trim();
    if (!/^NSA-/i.test(code)) { setRefPerk(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payment/perk-public?code=${encodeURIComponent(code)}`);
        if (!cancelled) setRefPerk(data);
      } catch { if (!cancelled) setRefPerk(null); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [referralCode]);

  useEffect(() => {
    // Meta ViewContent for the registration experience (fires once).
    trackViewContent("register", newEventId(), { content_name: "IdeaThon 2026 Registration" });
  }, []);

  // If admin hasn't launched yet, registration is closed — bounce to the Coming Soon landing.
  if (config && config.status === "pre_launch") return <Navigate to="/" replace />;
  // Registration deadline passed — show a friendly closed state + waitlist (new sign-ups only;
  // already-registered users can still log in below via the login link).
  if (config && config.registration_closed && mode === "register" && !user) {
    return (
      <div className="App" data-testid="register-closed">
        <Navbar />
        <div className="min-h-[72vh] flex items-center justify-center px-6 py-20 grid-bg">
          <div className="max-w-lg text-center fade-up">
            <div className="inline-flex items-center gap-2 badge badge-invited mb-6" data-testid="register-closed-badge">
              <Lock className="w-3.5 h-3.5" /> Registration closed
            </div>
            <h1 className="font-display text-4xl sm:text-5xl text-white leading-tight tracking-tight">Registrations are closed</h1>
            <p className="text-gray-400 mt-5 leading-relaxed">
              Registration for <span className="text-white">IdeaThon 2026</span> has closed. Drop your email and we&apos;ll notify you about future hackathons — you&apos;ll be first in line.
            </p>
            <div className="flex justify-center mt-8">
              <WaitlistForm testid="register-closed-waitlist" />
            </div>
            <p className="text-sm text-gray-500 mt-8">
              Already registered? <Link to="/register?mode=login" className="text-brand hover:underline" data-testid="register-closed-login-link">Log in here</Link>.
            </p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
  // If user is already logged in, send them to the right next step.
  if (user) {
    const dest = user.payment_status === "paid" ? "/team" : (user.college_verified ? "/payment" : "/verify");
    return <Navigate to={dest} replace />;
  }

  const validateClient = () => {
    if (mode === "register") {
      if (!name.trim() || name.trim().length < 2) return "Please enter your full name";
      if (!personalEmail.includes("@")) return "Enter a valid personal email";
      if (!collegeName.trim() || collegeName.trim().length < 2) return "Enter your college name";
    }
    if (!collegeEmail.includes("@")) return "Enter a valid college email";
    const domain = collegeEmail.split("@")[1]?.toLowerCase() || "";
    if (!config?.test_mode && DISPOSABLE.includes(domain)) return "College email must be from an institutional domain (not a personal email provider)";
    if (mode === "register" && personalEmail.toLowerCase() === collegeEmail.toLowerCase()) return "Personal email and college email must be different";
    if (mode === "register") {
      if (!phone.trim()) return "Phone / WhatsApp number is required";
      const d = phone.replace(/\D/g, "").replace(/^91/, "").replace(/^0/, "");
      if (d.length !== 10) return "Phone / WhatsApp must be a 10-digit Indian mobile number";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validateClient();
    if (err) return toast.error(err);
    setLoading(true);
    try {
      if (mode === "register") {
        const eventId = newEventId();
        const { data } = await api.post("/auth/register", {
          name, personal_email: personalEmail, college_name: collegeName, college_email: collegeEmail,
          linkedin_url: linkedinUrl.trim() || null, phone: phone.trim(),
          phone_country_code: "+91",
          hi_marketing_consent: hiConsent,
          consent_text_version: hiConsent ? "hi-marketing-v1" : null,
          ad_consent: getAdConsent() === "granted",
          referral_code: referralCode.trim() || null, meta_event_id: eventId,
        });
        toast.success(data.message || "Verification email sent");
        track("register_complete", { funnel_step: 1, method: "email", event_id: eventId });
        navigate(`/verify?email=${encodeURIComponent(data.college_email)}`);
      } else {
        const { data } = await api.post("/auth/login", { college_email: collegeEmail });
        toast.success("Verification code sent");
        navigate(`/verify?email=${encodeURIComponent(data.college_email)}`);
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setLoading(false); }
  };

  const facts = [
    { l: "Entry Fee", v: `₹${config?.entry_fee ?? FEE_DEFAULT}` },
    { l: "Team Size", v: `${config?.min_team_size ?? TEAM_MIN_DEFAULT}–${config?.max_team_size ?? TEAM_MAX_DEFAULT}` },
    { l: "Format", v: "Online" },
    { l: "Rounds", v: "2" },
  ];

  return (
    <div className="App" data-testid="register-page">
      <Navbar />
      <div className="grid lg:grid-cols-[43fr_57fr]">
        {/* LEFT: information */}
        <div className="relative hidden lg:flex flex-col px-12 xl:px-16 pt-20 pb-16 grid-bg overflow-hidden border-r border-white/10">
          <div className="relative max-w-md fade-up">
            <h1 className="font-display text-4xl xl:text-5xl text-white leading-tight tracking-tight">Register for IdeaThon 2026</h1>
            <p className="text-gray-400 mt-4 leading-relaxed max-w-sm">Create your account, verify your college email, and complete registration.</p>
            <div className="grid grid-cols-2 gap-3 mt-9">
              {facts.map((x) => (
                <div key={x.l} className="border border-white/10 p-4 transition-colors duration-200 hover:border-white/25">
                  <div className="label-eyebrow">{x.l}</div>
                  <div className="font-display text-2xl text-white mt-1 tabular-nums">{x.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: form */}
        <div className="flex items-start justify-center px-5 sm:px-6 pt-8 pb-16 lg:pt-20 lg:pb-24 lg:px-12">
          <div className="w-full max-w-[520px] fade-up">
            <div className="lg:hidden grid grid-cols-4 gap-2 mb-6" data-testid="register-mobile-facts">
              {facts.map((x) => (
                <div key={x.l} className="border border-white/10 px-2 py-2.5 text-center">
                  <div className="label-eyebrow !text-[0.6rem] !tracking-normal truncate">{x.l}</div>
                  <div className="font-display text-base text-white mt-0.5 tabular-nums">{x.v}</div>
                </div>
              ))}
            </div>
            <div className="flex border border-white/10 mb-6" data-testid="auth-tabs">
              <button
                type="button"
                onClick={() => setMode("register")}
                className={`flex-1 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset ${mode === "register" ? "bg-brand text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="tab-register"
              >Register</button>
              <button
                type="button"
                onClick={() => setMode("login")}
                className={`flex-1 py-3 text-sm font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset ${mode === "login" ? "bg-brand text-black" : "text-gray-400 hover:text-white"}`}
                data-testid="tab-login"
              >Sign in</button>
            </div>

            <h2 className="font-display text-3xl text-white">
              {mode === "register" ? "Create your account" : "Welcome back"}
            </h2>
            <p className="text-sm text-gray-500 mt-2">
              {mode === "register" ? "Step 1 of 3 · Account → Verify → Pay" : "We'll send a one-time code to your college email."}
            </p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              {mode === "register" && (
                <>
                  <div>
                    <label className="label-eyebrow block mb-2">Full name</label>
                    <div className="relative">
                      <User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name"
                        className="input-field !pl-10" required data-testid="register-name" />
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Personal email</label>
                    <div className="relative">
                      <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="email" value={personalEmail} onChange={(e) => setPersonalEmail(e.target.value)} placeholder="you@gmail.com"
                        className="input-field !pl-10" required data-testid="register-personal-email" />
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">College name</label>
                    <div className="relative">
                      <Building2 className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="text" value={collegeName} onChange={(e) => setCollegeName(e.target.value)} placeholder="Your college or university"
                        className="input-field !pl-10" required data-testid="register-college-name" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="label-eyebrow block mb-2">College email</label>
                <div className="relative">
                  <GraduationCap className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                  <input type="email" value={collegeEmail} onChange={(e) => setCollegeEmail(e.target.value)} placeholder="yourname@university.ac.in"
                    className="input-field !pl-10" required data-testid="register-college-email" />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">Use your college-issued email address. Personal email addresses aren't accepted.</p>
              </div>
              {mode === "register" && (
                <>
                  <div>
                    <label className="label-eyebrow block mb-2">LinkedIn profile URL <span className="text-gray-500 normal-case">(optional)</span></label>
                    <div className="relative">
                      <Linkedin className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="text" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="linkedin.com/in/yourname"
                        className="input-field !pl-10" data-testid="register-linkedin" />
                    </div>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Phone / WhatsApp number <span className="text-brand">*</span></label>
                    <div className="relative">
                      <Phone className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="10-digit mobile number" maxLength={15}
                        className="input-field !pl-10" required data-testid="register-phone" />
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">Used for account verification, payment support, and important IdeaThon updates.</p>
                  </div>
                  <div>
                    <label className="label-eyebrow block mb-2">Referral code <span className="text-gray-500 normal-case">(optional)</span></label>
                    <div className="relative">
                      <Gift className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                      <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value)} placeholder="e.g. NSA-XXXX-1234"
                        className={`input-field !pl-10 ${referralCode ? "!pr-10" : ""}`} data-testid="register-referral" />
                      {referralCode && (
                        <button type="button" aria-label="Remove referral code"
                          onClick={() => { setReferralCode(""); setRefPerk(null); clearReferral(); }}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white transition-colors"
                          data-testid="register-referral-clear">
                          <X className="w-4 h-4" strokeWidth={2} />
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">Got a code from a NoobSync ambassador? Enter it to credit them. You can clear it and enter a different one anytime.</p>
                    {refPerk?.valid && (
                      <div className="mt-2 flex items-start gap-2 text-sm text-emerald-400" data-testid="register-referral-preview">
                        <Gift className="w-4 h-4 mt-0.5 flex-shrink-0" strokeWidth={1.75} />
                        <span>
                          {refPerk.free
                            ? <>Your entry will be <strong className="text-white">FREE</strong> at checkout{refPerk.referrer_name ? <> (via {refPerk.referrer_name})</> : null}!</>
                            : <>You'll pay <strong className="text-white">₹{refPerk.final_price}</strong> at checkout — that's <strong className="text-white">₹{refPerk.savings} off{refPerk.discount_type === "percent_off" ? ` (${formatPercentOff(refPerk.base_price, refPerk.final_price)})` : ""}</strong>{refPerk.referrer_name ? <> (via {refPerk.referrer_name})</> : null}.</>}
                        </span>
                      </div>
                    )}
                    {refPerk && !refPerk.valid && ["expired", "sold_out"].includes(refPerk.reason) && (
                      <div className="mt-2 text-sm text-amber-400" data-testid="register-referral-preview">
                        This referral offer is {refPerk.reason === "sold_out" ? "fully claimed" : "expired"} — full price applies, but the ambassador is still credited.
                      </div>
                    )}
                  </div>
                </>
              )}
              {mode === "register" && (
                <label className="flex items-start gap-3 border border-white/10 p-4 cursor-pointer" data-testid="hi-marketing-consent-wrap">
                  <input type="checkbox" checked={hiConsent} onChange={(e) => setHiConsent(e.target.checked)}
                    className="mt-1 accent-[#FF6B2B] shrink-0" data-testid="hi-marketing-consent" />
                  <span className="text-xs text-gray-400 leading-relaxed">
                    I agree to receive information about NoobSync HI courses, events, internships, offers,
                    and career opportunities through WhatsApp, phone, SMS, and email. I can opt out at any time.
                    <span className="block mt-1 text-gray-600">Optional — not required to register for IdeaThon.</span>
                  </span>
                </label>
              )}
              <button type="submit" disabled={loading} className="btn-primary w-full min-h-[48px] flex items-center justify-center gap-2" data-testid="register-submit">
                {loading ? <span className="spinner" /> : null}
                {mode === "register" ? "Create account & verify email" : "Send verification code"}
              </button>
              <p className="text-xs text-center text-gray-500" data-testid="register-legal-line">
                By {mode === "register" ? "registering" : "continuing"}, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-white underline underline-offset-2" data-testid="register-terms-link">Terms &amp; Conditions</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-brand hover:text-white underline underline-offset-2" data-testid="register-privacy-link">Privacy Policy</a>.
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
